import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  faFileUpload,
  faCloudUploadAlt,
  faTimes,
  faSpinner,
  faCheck,
  faExclamationCircle,
} from '@fortawesome/free-solid-svg-icons';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { IDataPullType, FormViewService, IntegrationMappingService, SubmissionService } from '@xilo-mono/form-contracts';
import { ReplaySubject, takeUntil } from 'rxjs';
import { CustomerFormFacade } from '../../+state/customer-form/customer-form.facade';
import { ActivatedRoute } from '@angular/router';
import { set } from 'lodash';

@Component({
  selector: 'xilo-mono-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss', '../../form-viewer.component.scss'],
})
export class FileUploadComponent
  extends FieldType
  implements OnInit, OnDestroy {
  files: {
    file: File;
    status: 'ready' | 'uploading' | 'success' | 'error';
  }[] = [];

  fileUploadIcon = faFileUpload;
  cloudUploadIcon = faCloudUploadAlt;
  removeIcon = faTimes;

  icons = {
    file: faFileUpload,
    cloud: faCloudUploadAlt,
    remove: faTimes,
    uploading: faSpinner,
    success: faCheck,
    error: faExclamationCircle,
  };

  clientId;
  fileReader = new FileReader();

  get inputId(): string {
    return `file-upload-${this.field?.key}`;
  }
  private readonly destroyed$ = new ReplaySubject<boolean>(1);

  constructor(
    private customerFormFacade: CustomerFormFacade,
    private submissionService: SubmissionService,
    private integrationMappingService: IntegrationMappingService,
    private formViewService: FormViewService,
    private route: ActivatedRoute
  ) {
    super();
  }

  ngOnInit(): void {
    this.customerFormFacade.metadata$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((metadata) => {
        this.clientId = metadata?.clientId;
      });

    this.formViewService.formSubmitted$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        this.files = [];
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  filesSelected(event: any) {
    if (event.target.files) {
      const filesToAdd: FileList = event.target.files;
      const newFiles = [];
      for (let i = 0; i < filesToAdd.length; i++) {
        newFiles.push({
          file: filesToAdd.item(i),
          status: 'ready',
        });
      }
      this.files = this.files.concat(newFiles);
    }
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  private getFieldPath(field: FormlyFieldConfig): (string | number)[] {
    const path = [];
    let current = field;
    while (current && typeof current.key !== 'undefined' && current.key !== null) {
      path.unshift(current.key);
      current = current.parent;
    }
    return path;
  }

  uploadFiles() {
    const token = localStorage.getItem('token');
    const filesToUpload = this.files.filter((f) => f.status === 'ready');
    filesToUpload.forEach((f) => {
      f.status = 'uploading';
    });
    const clientId = this.clientId || +this.route.snapshot.queryParamMap.get('clientId');
    this.submissionService
      .uploadFiles(
        filesToUpload.map((f) => f.file),
        clientId,
        token
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          const isOCRFileReader = this.to?.isOCRFileReader;
          filesToUpload.forEach((f) => {
            f.status = 'success';
          });
          const uploadedFilenames = this.files
            .filter((f) => f.status === 'success')
            .map((f) => f.file.name)
            .join(', ');
          
          const valueToSet = isOCRFileReader && res?.encryptFileName 
                             ? res.encryptFileName 
                             : uploadedFilenames;

          this.formControl.setValue(valueToSet);

          // try {
          //   if (this.formViewService && this.formViewService.model) {
          //     const fullPath = this.getFieldPath(this.field);
          //     if (fullPath.length > 0) {
          //       set(this.formViewService.model, fullPath, valueToSet);
          //     } else {
          //       console.warn('FileUploadComponent: Could not determine full path for field key:', this.field?.key, '. Cannot update formViewService.model directly.');
          //     }
          //   } else {
          //     console.warn('FileUploadComponent: formViewService.model not available. Cannot update root model directly.');
          //   }
          // } catch (error) {
          //   console.error('FileUploadComponent: Error updating formViewService.model:', error);
          // }
          
          this.integrationMappingService.initDataPull(
            this.to?.pullDataTrigger,
            this.to?.dataPullMappingId,
            this.field?.parent?.parent?.templateOptions?.subIndex,
            true,
            {
              type: isOCRFileReader ? 'OCR' : 'DATA',
              loadSeconds: isOCRFileReader ? 120: 10,
            } as IDataPullType
          );
        },
        error: () => {
          filesToUpload.forEach((f) => {
            f.status = 'error';
          });
        },
      });
  }
}
